/**
 * Message Vault — types.
 *
 * The vault is a continuous, append-only mirror of every WhatsApp
 * message body we observe arrive in this browser. The mental model
 * is a backup system: messages flow IN as they arrive, and when WA
 * later shows a "this message was deleted" placeholder, we look up
 * the body from the backup and offer to display it.
 *
 *   capture flow   :   Store.Msg.on('add')  →  vault.put(msgId, body)
 *   restore flow   :   DOM scan for revoked placeholders
 *                          →  vault.get(msgId)
 *                          →  show body in popup
 *
 * Deliberately NOT tracked here:
 *   - deletion events (we infer deletion from the DOM placeholder)
 *   - edit history (Phase 2)
 *   - outgoing messages (Phase 2 — current request is incoming only)
 *
 * Stored ONLY in this user's IndexedDB. Never synced anywhere.
 */
import { canonicalMsgKey } from './resolve-msg-id';

/** Stable identity for a message. Same value WhatsApp uses everywhere
 *  (`msg.id._serialized`). */
export type WaMessageId = string;
export type WaChatId = string;

/** Persisted mapping from a WA-placeholder `data-id` (in the DOM) to
 *  the backed-up body it represents.
 *
 *  Why this table exists: WA assigns a brand-new data-id to revoked
 *  message placeholders, unrelated to the original message's id. We
 *  reconstruct the link by temporal correlation at the moment of the
 *  deletion event (see `recentDeletions` queue + the chat-based body
 *  lookup in the deletion thunk). Once that link is made, persisting
 *  it here means the chip survives:
 *    - WA virtualization re-mounting the row on scroll
 *    - chat switches and back
 *    - page reload */
export interface PlaceholderMapping {
  id?: number;
  /** The data-id WA puts on the revoked placeholder row in the DOM. */
  placeholderDataId: string;
  /** Chat this mapping belongs to (for per-chat wipe). */
  chatId: WaChatId;
  /** The original captured body the chip should show. */
  body: string;
  /** Media-message caption (WAWeb keeps it separate from `body`, which
   *  holds the thumbnail base64 for media messages). Optional —
   *  additive, absent on rows recorded before this field existed. */
  caption?: string;
  /** Voice-note audio bytes (base64) captured at arrival time — see
   *  BackedUpMessage.audioBase64. Duplicated into the mapping so the
   *  recovered audio survives page reloads without a vault re-match. */
  audioBase64?: string;
  audioMimetype?: string;
  hadMedia: boolean;
  authorName: string;
  /** WA's reported send time of the original message, in ms. */
  whatsappTimestamp: number;
  /** When the mapping was recorded. */
  recordedAt: number;
}

/** A single backed-up message row.
 *
 *  ONE table, ONE row per observed message. The vault never mutates a
 *  row after writing it — the first capture wins, because that's the
 *  body the user saw. Subsequent observations of the same `msgId`
 *  (history replays after tab reload) hit Dexie's unique-constraint
 *  on `msgId` and silently no-op.
 *
 *  Field naming: we use `msgId` internally (matches WA's `id._serialized`
 *  vocabulary in this codebase). The spec calls this `messageKey` —
 *  same concept. */
export interface BackedUpMessage {
  /** Auto-increment primary key. */
  id?: number;

  /** WA-stable serialized id — the first-writer's addressing (@c.us or @lid).
   *  Unique within the table. (Spec: `messageKey`.) */
  msgId: WaMessageId;
  /** Addressing-agnostic identity `<fromMe>_<stanzaId>` (see `canonicalMsgKey`).
   *  The dedup key that collapses the @c.us/@lid variants of one message to a
   *  single row. Indexed (vault v6). */
  dedupKey: string;
  chatId: WaChatId;

  /** JID of whoever produced the message. For groups this is the
   *  participant who sent; for one-on-ones it matches `chatId` for
   *  incoming and the self-JID for outgoing. */
  senderId: string;

  /** WA message type — 'chat' (plain text), 'image', 'video', 'ptt',
   *  'audio', 'document', 'sticker', 'location', etc. Used by the
   *  matching fallback (PHASE 4) to skip type-mismatched candidates. */
  type: string;

  /** The message's `body` as WAWeb stores it. For plain-text messages
   *  this is the text; for MEDIA messages WAWeb puts the base64 of the
   *  thumbnail here — which is what makes deleted images partially
   *  recoverable (the recovery modal detects and renders it). */
  body: string;

  /** Media-message caption (`msg.caption`). Kept separately so the
   *  thumbnail in `body` doesn't shadow the human text. Optional —
   *  additive, non-indexed (no Dexie migration needed); absent on
   *  rows captured before this field existed. */
  caption?: string;

  /** Voice-note audio bytes, base64-encoded, captured at ARRIVAL time
   *  (voice notes have no text body and no thumbnail — the audio IS
   *  the recoverable artifact). Only populated for incoming 'ptt' /
   *  'audio' messages under the size cap (see MAX_VAULT_AUDIO_BYTES
   *  in the capture thunk). Optional + non-indexed — no migration.
   *  Retention purge deletes the whole row, audio included. */
  /**
   * The SENDER'S CORRELATION TOKEN, decoded out of WhatsApp's
   * `messageContextInfo.messageSecret` at capture time.
   *
   * Stored here because the vault is what actually answers most reads: a chat
   * WhatsApp has evicted from its in-memory store (anything not open, which is
   * nearly everything within the hour) is served from this table, and a field
   * we do not capture is a field the API can never return. That is exactly how
   * `secret` came back null for a message whose token WAS on the live message —
   * the live copy was gone and the vault copy never had it.
   *
   * Only ever set for messages WE sent with a caller token (the marker gates it
   * — see bt-common scheduling/markers.ts); absent for everything else, so no
   * WhatsApp E2E material is ever written here.
   *
   * NOT indexed: nothing queries by it, and an unindexed Dexie field needs no
   * schema version bump — old rows simply read back without it.
   */
  secret?: string;
  audioBase64?: string;
  /** Normalised mimetype for `audioBase64` (e.g. 'audio/ogg'). */
  audioMimetype?: string;

  /** Whether the original message had a media attachment we couldn't
   *  preserve. Drives the UI hint ("there was also a picture"). */
  hadMedia: boolean;

  /** Best-effort author display name resolved at capture time. */
  authorName: string;

  /** True for messages sent FROM this account (`msg.id.fromMe`). */
  fromMe: boolean;

  /** Send time per WA, in ms since epoch. */
  whatsappTimestamp: number;

  /** Wall-clock time the row entered our DB. Drives retention.
   *  (Spec: `createdAt`.) */
  capturedAt: number;

  /* ── Backup sync flags (vault v4) ─────────────────────────────────
   *  These drive the daily server backup (Track 1.3). Selection is by
   *  FLAG, never by timestamp — see the design doc: `synced=0` rows are
   *  the backup work queue, marked `1` per-chunk only after the server
   *  ACKs. Both are INDEXED (`[synced+capturedAt]`) so the uploader can
   *  range-scan the unsynced queue without a full-table read.
   *
   *  Stored as 0|1 (not boolean) because IndexedDB cannot index a
   *  boolean key path — Dexie requires a number/string for indexes. */
  /** 0 = message row not yet uploaded to the server DB; 1 = uploaded. */
  synced: 0 | 1;
  /** 0 = media bytes (if any) not yet uploaded to the bucket; 1 = done. */
  mediaSynced: 0 | 1;
  /** Bucket object path once the media bytes are uploaded (content-hash
   *  key). Present only after a successful media-phase upload. */
  mediaPointer?: string;
  /** Epoch ms when the uploaded media object expires (mediaTtlDays). */
  mediaExpiresAt?: number;
  /** WHY this row's media isn't in the bucket, stamped by the backup engine so
   *  the reason is authoritative + shown to the user (not a UI guess). One of
   *  [[MEDIA_SKIP_REASONS]]; cleared once the media backs up. Absent = either
   *  backed up (see `mediaPointer`) or still pending. */
  mediaSkipReason?: MediaSkipReason;
}

/** Stable reason codes for why a media file wasn't backed up. UI maps them to
 *  localized end-user text. Kept as codes (not text) so the copy lives in one
 *  place and can be translated. */
export const MEDIA_SKIP_REASONS = ['category-off', 'too-large', 'unavailable'] as const;
export type MediaSkipReason = typeof MEDIA_SKIP_REASONS[number];

/** Input accepted by `captureMessage`. The caller supplies the observed
 *  message fields; the vault fills `id`/`capturedAt` and defaults the
 *  backup sync flags (`synced`/`mediaSynced` → 0) via `normalizeVaultRow`.
 *  Callers MAY pre-set the flags/pointer (e.g. a re-import of an already
 *  synced row) but normally leave them unset. */
export type VaultCaptureInput = Omit<
  BackedUpMessage,
  'id' | 'capturedAt' | 'synced' | 'mediaSynced' | 'dedupKey'
> & {
  /** Normally derived from `msgId` by `normalizeVaultRow`; callers may omit. */
  dedupKey?: string;
  synced?: 0 | 1;
  mediaSynced?: 0 | 1;
  mediaPointer?: string;
  mediaExpiresAt?: number;
};

/** Fill the backup sync flags with their unsynced defaults.
 *
 *  Pure + total: any value that isn't exactly the number `1` collapses
 *  to `0`, so rows written before v4 (flags absent/garbage) and freshly
 *  captured rows both land as "needs backup". Used both by the Dexie v4
 *  `upgrade` callback and by `captureMessage` at write time so there is
 *  ONE definition of the default. */
/** Media types whose FULL bytes are backed up to the bucket by downloading them
 *  from WhatsApp at upload time (they aren't in the vault — only a thumbnail).
 *  Audio/ptt are handled separately via `audioBase64` (bytes captured up front). */
export const BUCKET_MEDIA_TYPES = new Set(['image', 'video', 'document', 'sticker', 'gif']);

/** True when a message TYPE carries bucket-backupable media. Eligibility keys on
 *  this (reliable) rather than on the `hadMedia` flag, which WhatsApp doesn't
 *  always populate at capture time — so an image whose `hasMedia`/`mediaKey`
 *  weren't loaded when we saw it (`hadMedia=false`) is STILL treated as media.
 *  That flag being unreliable is exactly why ~58% of images were silently
 *  excluded from backup. */
export const isBucketMediaType = (type: unknown): boolean =>
  typeof type === 'string' && BUCKET_MEDIA_TYPES.has(type);

export const normalizeVaultRow = <
  T extends {
    synced?: unknown;
    mediaSynced?: unknown;
    audioBase64?: unknown;
    hadMedia?: unknown;
    type?: unknown;
    msgId?: unknown;
    dedupKey?: unknown;
  },
>(
  row: T,
): T & { synced: 0 | 1; mediaSynced: 0 | 1; dedupKey: string } => ({
  ...row,
  synced: row.synced === 1 ? 1 : 0,
  // A row needs a media upload when it carries voice-note bytes (audioBase64) OR
  // is an image/video/document/sticker (full bytes downloaded on demand at upload
  // time). Everything else (text, plus already-uploaded rows) is stamped
  // mediaSynced=1, so the uploader's media-phase query (`mediaSynced=0`) returns
  // ONLY rows still pending a bucket upload — never the whole vault.
  // Queue a media upload for voice-note bytes (audioBase64) OR any bucket media
  // TYPE — deliberately NOT gated on `hadMedia`, which is unreliable at capture
  // (an image seen before its media metadata loaded lands hadMedia=false and
  // would otherwise never be backed up).
  mediaSynced:
    row.mediaSynced === 1
      ? 1
      : row.audioBase64 || isBucketMediaType(row.type)
      ? 0
      : 1,
  // Addressing-agnostic dedup identity, derived from msgId when not supplied.
  dedupKey:
    typeof row.dedupKey === 'string' && row.dedupKey.length > 0
      ? row.dedupKey
      : canonicalMsgKey(typeof row.msgId === 'string' ? row.msgId : ''),
});

/** Shape returned by the recovery API per PHASE 5 of the spec.
 *  Decoupled from the DB row so the public surface is stable even if
 *  we change BackedUpMessage internals later. */
export interface RecoveryResult {
  recovered: boolean;
  originalText: string;
  originalTimestamp: number;
  originalMessage: BackedUpMessage | null;
  /** Which matching tier succeeded — for diagnostics and UI hints. */
  matchedBy?: 'messageKey' | 'placeholder' | 'context' | 'recent';
}
